import { useState } from "react";
import { Link } from "react-router-dom";
import { LoginForm, ProFormText } from "@ant-design/pro-components";
import { MailOutlined } from "@ant-design/icons";
import { Alert, theme } from "antd";

import BrandLogo from "@/components/BrandLogo";
import CaptchaWidget from "@/components/CaptchaWidget";
import {
  CAPTCHA_PROMPT,
  useCaptchaConfig,
  useCaptchaToken,
} from "@/lib/captcha";
import { apiErrorMessage } from "@/lib/errors";

import { forgotPassword } from "./api";

/** Request a password-reset link. Always shows success (non-enumerating). */
function ForgotPasswordPage() {
  const { token } = theme.useToken();
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const captcha = useCaptchaConfig();
  const captchaToken = useCaptchaToken();

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: token.colorBgLayout,
      }}
    >
      <LoginForm<{ email: string }>
        logo={<BrandLogo />}
        title="Reset your password"
        subTitle="We'll email you a reset link"
        submitter={{
          searchConfig: { submitText: "Send reset link" },
          submitButtonProps: { loading: submitting, disabled: sent },
        }}
        onFinish={async (values) => {
          if (captcha && !captchaToken.token) {
            setFormError(CAPTCHA_PROMPT);
            return true;
          }
          const usedToken = captchaToken.token ?? undefined;
          setSubmitting(true);
          try {
            await forgotPassword(values.email, usedToken);
            setFormError(null);
            setSent(true);
          } catch (error) {
            // A CAPTCHA token is single-use.
            if (usedToken) captchaToken.reset();
            setFormError(
              apiErrorMessage(error, "We couldn't send the link. Please try again."),
            );
          } finally {
            setSubmitting(false);
          }
          return true;
        }}
        actions={<Link to="/login">Back to sign in</Link>}
      >
        {formError && !sent ? (
          <Alert type="error" showIcon message={formError} style={{ marginBottom: 24 }} />
        ) : null}
        {sent ? (
          <Alert
            type="success"
            showIcon
            style={{ marginBottom: 24 }}
            message="Check your email"
            description="If an account exists for that address, a reset link is on its way. It's valid for 30 minutes."
          />
        ) : (
          <>
            <ProFormText
              name="email"
              fieldProps={{ size: "large", prefix: <MailOutlined /> }}
              placeholder="Email"
              rules={[
                { required: true, message: "Please enter your email" },
                { type: "email", message: "Enter a valid email" },
              ]}
            />
            {captcha ? (
              <CaptchaWidget
                provider={captcha.provider}
                siteKey={captcha.siteKey}
                onToken={captchaToken.setToken}
                resetKey={captchaToken.resetKey}
              />
            ) : null}
          </>
        )}
      </LoginForm>
    </div>
  );
}

export default ForgotPasswordPage;
